import { useMemo } from 'react'
import useSWR from 'swr'
import {
  SocialTopicFilters,
  SocialTopicSeriesBucket,
  SocialTopicSeriesQuery,
  SocialTopicSeriesResponse,
  SocialUniverseItem,
} from '@digital-twins/shared-types'
import { fillBuckets, leadingCell, SocialFilledBucket } from './charts/socialTopicMetrics'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { SocialTopicSlotContext } from './SocialTopicView'
import { SocialTopicFacet } from './useSocialTopicFilters'
import {
  SOCIAL_UNIVERSE_COUNTS_SWR_KEY,
  socialCountsQueryKey,
  useSocialUniverseCounts,
} from './useSocialUniverse'

/**
 * The series shares the counts key family (`SOCIAL_UNIVERSE_COUNTS_SWR_KEY`,
 * token, query, refresh key), so whatever refetches a Topic's counts (a new
 * `refreshKey`, or a hide's `isSocialUniverseCountsKey` mutate) refetches the
 * series with them: the page never mixes old and new numbers.
 */
export function socialTopicSeriesSwrKey(
  token: string,
  query: SocialTopicSeriesQuery,
  refreshKey: string | number | null,
): [string, string, string, string | number | null] {
  const countsPart = socialCountsQueryKey({ groupBy: 'channel', filters: query.filters })
  return [
    SOCIAL_UNIVERSE_COUNTS_SWR_KEY,
    token,
    `series:${JSON.stringify([countsPart, query.bucket ?? null, query.brandShareOf ?? null, query.split ?? null])}`,
    refreshKey,
  ]
}

interface SocialTopicSeriesQueryOptions {
  /** The brand facet's leader (`leadingCell`), what the KPI strip's share of voice names. */
  leadingBrandId?: string | null
  /** Absent: the server picks it from the publish span. */
  bucket?: SocialTopicSeriesBucket
}

/**
 * The Topic page's series query under `filters`. Asks for share of voice with
 * the filtered brand, else the brand facet's leader (the KPI strip's brand),
 * else the Topic's first brand: `brandedPosts` (posts naming any brand) is the
 * same whichever brand is asked, so the KPI strip and the density chart read
 * one request. No brands, no share of voice.
 */
export function socialTopicSeriesQuery(
  universe: Pick<SocialUniverseItem, 'brands'>,
  filters: SocialTopicFilters,
  options: SocialTopicSeriesQueryOptions = {},
): SocialTopicSeriesQuery {
  const { leadingBrandId, bucket } = options
  const brandIds = universe.brands.map((brand) => brand.id)
  const pick = (brandId: string | null | undefined) =>
    brandId && brandIds.includes(brandId) ? brandId : undefined
  const brandShareOf = pick(filters.brandId) ?? pick(leadingBrandId) ?? brandIds[0]
  return {
    filters,
    ...(bucket ? { bucket } : {}),
    ...(brandShareOf ? { brandShareOf } : {}),
  }
}

interface UseSocialTopicSeriesQueryOptions {
  /** Leave this dimension's own filter out (a KPI that is a share of it). */
  excluding?: SocialTopicFacet
  bucket?: SocialTopicSeriesBucket
}

/**
 * The series query a Topic page slot reads. Without a brand filter it waits
 * for the brand facet (the same SWR key the filter row and KPI strip read),
 * so share of voice names the leading brand the KPI strip shows. Null while
 * that facet loads.
 */
export function useSocialTopicSeriesQuery(
  context: Pick<SocialTopicSlotContext, 'universe' | 'topicFilters' | 'refreshKey' | 'brandNames'>,
  options: UseSocialTopicSeriesQueryOptions = {},
): SocialTopicSeriesQuery | null {
  const { universe, topicFilters, refreshKey, brandNames } = context
  const { excluding, bucket } = options

  const hasBrands = universe.brands.length > 0
  const isBrandFiltered = Boolean(topicFilters.filters.brandId)
  const brandFacetFilters = useMemo(() => topicFilters.facetFilters('brand'), [topicFilters])
  const { counts: brandCounts } = useSocialUniverseCounts(
    hasBrands && !isBrandFiltered ? universe.token : null,
    { groupBy: 'brand', filters: brandFacetFilters },
    refreshKey,
  )
  const leadingBrandId = brandCounts
    ? leadingCell(brandCounts.cells, (brandId) => brandNames.get(brandId) ?? brandId)?.key ?? null
    : null
  const isWaiting = hasBrands && !isBrandFiltered && !brandCounts
  const filters = useMemo(() => topicFilters.facetFilters(excluding), [topicFilters, excluding])

  return useMemo(
    () =>
      isWaiting ? null : socialTopicSeriesQuery(universe, filters, { leadingBrandId, bucket }),
    [isWaiting, universe, filters, leadingBrandId, bucket],
  )
}

interface UseSocialTopicSeriesResult {
  series: SocialTopicSeriesResponse | null
  /**
   * Gap-filled, oldest first (`fillBuckets`), across the series' `range`:
   * the Topic's period narrowed by the query's window (D23), so a chart's
   * axis is the period, never only the days that have posts.
   */
  buckets: Array<SocialFilledBucket>
  /**
   * The same, across rows with posts only. Under a brand filter with share of
   * voice, a row can count branded posts but no posts; this range leaves
   * those edge buckets out, so the density axis spans the posts it draws.
   */
  postBuckets: Array<SocialFilledBucket>
  /** Posts naming any brand under every filter but brand; null without share of voice. */
  brandedPosts: number | null
  isLoading: boolean
  hasError: boolean
}

/**
 * A Topic's posts per publish-date bucket and channel. Keeps the previous
 * series on screen while a new filter loads. Null `token` or `query` makes no request.
 */
export function useSocialTopicSeries(
  token: string | null,
  query: SocialTopicSeriesQuery | null,
  refreshKey: string | number | null = null,
): UseSocialTopicSeriesResult {
  const socialListeningApi = useSocialListeningApi()
  const { data, error, isLoading } = useSWR(
    token && query ? socialTopicSeriesSwrKey(token, query, refreshKey) : null,
    () => socialListeningApi.getUniverseSeries(token as string, query as SocialTopicSeriesQuery),
    { revalidateOnFocus: false, keepPreviousData: true },
  )
  const series = data ?? null

  const buckets = useMemo(
    () => (series ? fillBuckets(series.buckets, series.bucket, series.range) : []),
    [series],
  )
  const postBuckets = useMemo(
    () =>
      series
        ? fillBuckets(
            series.buckets.filter((row) => row.posts > 0),
            series.bucket,
          )
        : [],
    [series],
  )
  const hasShareOfVoice = Boolean(query?.brandShareOf)
  const brandedPosts = useMemo(() => {
    if (!series || !hasShareOfVoice) {
      return null
    }
    return buckets.reduce((sum, entry) => sum + entry.total.brandedPosts, 0)
  }, [series, buckets, hasShareOfVoice])

  return {
    series,
    buckets,
    postBuckets,
    brandedPosts,
    isLoading: Boolean(token) && (isLoading || !query),
    hasError: Boolean(error),
  }
}
